/* eslint-disable @nx/enforce-module-boundaries */
import { CommonModule } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription, catchError, filter, of, switchMap } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  KanziUiTopBarComponent,
  MainMenuService,
  KanziUiMainSidebarComponent,
  LayoutService,
  KanziUiBreadcrumbComponent,
  KanziUiModule,
} from '@kanzi-apes/kanzi-ui';
import { KanziPrimeUiModule, MenuItem } from '@kanzi-apes/kanzi-prime-ui';
import * as Auth from '@kanzi-apes/kanzi-authenticator';
import { ItemsMenu } from '../configs/main-menu';
import * as layoutStore from '../+state/layout/layout.reducer';

/**
 * Root shell component for the Kanzi Dashboards application.
 * Renders the topbar, sidebar, breadcrumb and the routed content,
 * handles session validation on init, builds the permission-filtered menu,
 * manages the layout container CSS classes and menu interaction, and
 * handles logout and language changes.
 */
@Component({
  selector: 'kanzi-apes-layout',
  imports: [
    CommonModule,
    KanziPrimeUiModule,
    KanziUiModule,
    KanziUiTopBarComponent,
    KanziUiMainSidebarComponent,
    KanziUiBreadcrumbComponent,
    TranslateModule,
  ],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
})
export class LayoutComponent implements OnInit, OnDestroy {
  private readonly translateService = inject(TranslateService);
  private readonly mainMenuService = inject(MainMenuService);
  private readonly storeAuth: Store<Auth.AuthState> = inject(Store);
  private readonly storeLayout: Store<layoutStore.LayoutState> = inject(Store);
  private readonly authService = inject(Auth.AuthService);
  private readonly router = inject(Router);
  readonly layoutService = inject(LayoutService);

  progressBar = signal(true);
  sessionReady = signal(false);
  itemsMenu = signal<MenuItem[]>([]);
  userProfile = this.storeAuth.selectSignal(Auth.getAuthUser);

  private overlayMenuOpenSubscription: Subscription;
  private topbarMenuOpenSubscription: Subscription;
  private menuProfileOpenSubscription: Subscription;
  private sessionSubscription: Subscription | null = null;
  private menuOutsideClickListener: (() => void) | null = null;
  private menuScrollListener: (() => void) | null = null;
  private topbarMenuOutsideClickListener: (() => void) | null = null;
  private menuProfileOutsideClickListener: (() => void) | null = null;

  @ViewChild(KanziUiTopBarComponent) appTopbar!: KanziUiTopBarComponent;
  @ViewChild(KanziUiMainSidebarComponent)
  appSidebar!: KanziUiMainSidebarComponent;

  /**
   * Sets up the menu interaction listeners (overlay menu, topbar menu and
   * profile menu) and resets the open menus on each route navigation.
   * @param renderer - Angular Renderer2 used to attach document-level listeners
   */
  constructor(public renderer: Renderer2) {
    this.hideMenuProfile();

    this.overlayMenuOpenSubscription =
      this.layoutService.overlayOpen$.subscribe(() => {
        this.hideTopbarMenu();

        if (!this.menuOutsideClickListener) {
          this.menuOutsideClickListener = this.renderer.listen(
            'document',
            'click',
            (event) => {
              const isOutsideClicked = !(
                this.appSidebar.el.nativeElement.isSameNode(event.target) ||
                this.appSidebar.el.nativeElement.contains(event.target) ||
                this.appTopbar.menuButton.nativeElement.isSameNode(
                  event.target
                ) ||
                this.appTopbar.menuButton.nativeElement.contains(event.target)
              );
              if (isOutsideClicked) {
                this.hideMenu();
              }
            }
          );
        }

        if (
          (this.layoutService.isHorizontal() ||
            this.layoutService.isSlim() ||
            this.layoutService.isSlimPlus()) &&
          !this.menuScrollListener
        ) {
          this.menuScrollListener = this.renderer.listen(
            this.appSidebar.menuContainer.nativeElement,
            'scroll',
            () => {
              if (this.layoutService.isDesktop()) {
                this.hideMenu();
              }
            }
          );
        }

        if (this.layoutService.state.staticMenuMobileActive) {
          this.blockBodyScroll();
        }
      });

    this.topbarMenuOpenSubscription =
      this.layoutService.topbarMenuOpen$.subscribe(() => {
        if (!this.topbarMenuOutsideClickListener) {
          this.topbarMenuOutsideClickListener = this.renderer.listen(
            'document',
            'click',
            (event) => {
              const isOutsideClicked = !(
                this.appTopbar.el.nativeElement.isSameNode(event.target) ||
                this.appTopbar.el.nativeElement.contains(event.target) ||
                this.appTopbar.mobileMenuButton.nativeElement.isSameNode(
                  event.target
                ) ||
                this.appTopbar.mobileMenuButton.nativeElement.contains(
                  event.target
                )
              );
              if (isOutsideClicked) {
                this.hideTopbarMenu();
              }
            }
          );
        }

        if (this.layoutService.state.staticMenuMobileActive) {
          this.blockBodyScroll();
        }
      });

    this.menuProfileOpenSubscription =
      this.layoutService.menuProfileOpen$.subscribe(() => {
        this.hideMenu();

        if (!this.menuProfileOutsideClickListener) {
          this.menuProfileOutsideClickListener = this.renderer.listen(
            'document',
            'click',
            (event) => {
              const isOutsideClicked = !(
                this.appSidebar.menuProfile.el.nativeElement.isSameNode(
                  event.target
                ) ||
                this.appSidebar.menuProfile.el.nativeElement.contains(
                  event.target
                )
              );
              if (isOutsideClicked) {
                this.hideMenuProfile();
              }
            }
          );
        }
      });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.hideMenu();
        this.hideTopbarMenu();
        this.hideMenuProfile();
      });
  }

  /**
   * Builds the dynamic CSS class map applied to the layout container,
   * driven by the current layout configuration and state.
   * @returns A map of CSS class names to their active state
   */
  get containerClass(): { [key: string]: boolean } {
    const styleClass: { [key: string]: boolean } = {
      'layout-overlay': this.layoutService.config().menuMode === 'overlay',
      'layout-static': this.layoutService.config().menuMode === 'static',
      'layout-slim': this.layoutService.config().menuMode === 'slim',
      'layout-slim-plus': this.layoutService.config().menuMode === 'slim-plus',
      'layout-horizontal':
        this.layoutService.config().menuMode === 'horizontal',
      'layout-reveal': this.layoutService.config().menuMode === 'reveal',
      'layout-drawer': this.layoutService.config().menuMode === 'drawer',
      'p-input-filled': this.layoutService.config().inputStyle === 'filled',
      'p-ripple-disabled': !this.layoutService.config().ripple,
      'layout-static-inactive':
        this.layoutService.state.staticMenuDesktopInactive &&
        this.layoutService.config().menuMode === 'static',
      'layout-overlay-active': this.layoutService.state.overlayMenuActive,
      'layout-mobile-active': this.layoutService.state.staticMenuMobileActive,
      'layout-topbar-menu-active': this.layoutService.state.topbarMenuActive,
      'layout-menu-profile-active': this.layoutService.state.menuProfileActive,
      'layout-sidebar-active': this.layoutService.state.sidebarActive,
      'layout-sidebar-anchored': this.layoutService.state.anchored,
    };
    styleClass['layout-topbar-' + this.layoutService.config().topbarTheme] =
      true;
    styleClass['layout-menu-' + this.layoutService.config().menuTheme] = true;
    styleClass[
      'layout-menu-profile-' + this.layoutService.config().menuProfilePosition
    ] = true;
    return styleClass;
  }

  /**
   * Adds the CSS class that prevents the document body from scrolling,
   * used while a mobile overlay menu is open.
   */
  blockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.add('blocked-scroll');
    } else {
      document.body.className += ' blocked-scroll';
    }
  }

  /**
   * Removes the CSS class that prevents the document body from scrolling.
   */
  unblockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.remove('blocked-scroll');
    } else {
      document.body.className = document.body.className.replace(
        new RegExp(
          '(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)',
          'gi'
        ),
        ' '
      );
    }
  }

  /**
   * Closes the sidebar menu, resets its active state and removes any
   * document-level listeners attached while the menu was open.
   */
  hideMenu(): void {
    this.layoutService.state.overlayMenuActive = false;
    this.layoutService.state.staticMenuMobileActive = false;
    this.layoutService.state.menuHoverActive = false;
    this.mainMenuService.reset();

    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
      this.menuOutsideClickListener = null;
    }

    if (this.menuScrollListener) {
      this.menuScrollListener();
      this.menuScrollListener = null;
    }
    this.unblockBodyScroll();
  }

  /**
   * Closes the topbar menu and removes its outside-click listener.
   */
  hideTopbarMenu(): void {
    this.layoutService.state.topbarMenuActive = false;

    if (this.topbarMenuOutsideClickListener) {
      this.topbarMenuOutsideClickListener();
      this.topbarMenuOutsideClickListener = null;
    }
  }

  /**
   * Closes the profile menu and removes its outside-click listener.
   */
  hideMenuProfile(): void {
    this.layoutService.state.menuProfileActive = false;

    if (this.menuProfileOutsideClickListener) {
      this.menuProfileOutsideClickListener();
      this.menuProfileOutsideClickListener = null;
    }
  }

  /** @inheritdoc */
  ngOnInit(): void {
    this.progressBar.set(true);
    this.itemsMenu.set(ItemsMenu);
    this.sessionSubscription = this.authService
      .checkAndRefreshToken()
      .pipe(
        switchMap((session) => {
          if (session && session.isValid()) {
            return this.authService.getCurrentUserData();
          }
          this.storeAuth.dispatch(Auth.loginRedirect());
          return of(null);
        }),
        catchError(() => {
          this.storeAuth.dispatch(Auth.loginRedirect());
          return of(null);
        })
      )
      .subscribe((userData) => {
        if (userData) {
          const displayUser = {
            ...userData,
            Username: this.buildDisplayName(userData),
          };
          this.storeAuth.dispatch(
            Auth.userProfileSuccess({ user: displayUser })
          );
          this.sessionReady.set(true);
        }
        this.progressBar.set(false);
      });
  }

  /**
   * Builds a human-readable display name from the Cognito user data.
   * Prefers given_name + family_name, falls back to email, and finally to
   * the raw Username. Used so the sidebar shows a readable name instead of
   * the Cognito UUID username.
   * @param userData - The raw user data returned by Cognito getUserData
   * @returns A readable display name
   */
  private buildDisplayName(userData: {
    Username: string;
    UserAttributes?: { Name: string; Value: string }[];
  }): string {
    const attrs = userData.UserAttributes ?? [];
    const getAttr = (name: string): string =>
      attrs.find((a) => a.Name === name)?.Value ?? '';
    const fullName = `${getAttr('given_name')} ${getAttr(
      'family_name'
    )}`.trim();
    return fullName || getAttr('email') || userData.Username;
  }

  /**
   * Dispatches the logout action to clear the session and redirect to login.
   */
  logoutSession(): void {
    this.storeAuth.dispatch(Auth.logout());
  }

  /**
   * Changes the active application language.
   * @param lang - The language code to apply (e.g. 'es', 'en')
   */
  changeLanguage(lang: string): void {
    this.translateService.use(lang);
  }

  /** @inheritdoc */
  ngOnDestroy(): void {
    if (this.sessionSubscription) {
      this.sessionSubscription.unsubscribe();
    }
    if (this.overlayMenuOpenSubscription) {
      this.overlayMenuOpenSubscription.unsubscribe();
    }
    if (this.topbarMenuOpenSubscription) {
      this.topbarMenuOpenSubscription.unsubscribe();
    }
    if (this.menuProfileOpenSubscription) {
      this.menuProfileOpenSubscription.unsubscribe();
    }
    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
    }
    if (this.menuScrollListener) {
      this.menuScrollListener();
    }
    if (this.topbarMenuOutsideClickListener) {
      this.topbarMenuOutsideClickListener();
    }
    if (this.menuProfileOutsideClickListener) {
      this.menuProfileOutsideClickListener();
    }
  }
}
